import {
  ArrowDropDown,
  ArrowRight,
  CallSplitOutlined,
  CrisisAlert,
  SavingsOutlined,
  TimerOutlined
} from '@mui/icons-material';
import { ReactElement, useEffect, useState } from 'react';
import styled from 'styled-components';
import COLORS from '../../colors';
import {
  DEFINE_OPTION,
  DURATION_SECONDS,
  MONTH_LOWERCASE
} from '../../constants';
import DefineInputChip from '../DefineInput/DefineInputChip';
import { Zeros } from '../Icons';
import { Input } from '../Input';
import { Button } from '../Button';
import { Grid } from '@mui/material';
import DefineDurationTable from './DefineDurationTable';
import { RootState } from '../../redux/store';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { rowStructure } from '../../redux/features/burst/types';
import {
  setAllRows,
  updateBudget,
  updateImpact,
  updateTarps,
  updateTotalCalculatedBudget,
  updateTotalCalculatedImpacts,
  updateTotalCalculatedTarps
} from '../../redux/features/burst/burstSlice';
import { Week } from '../../services/date/types';
import { getMonthName } from '../../services/date';
import {
  isOnlyNumbers,
  mapPascalMonthToLowercaseMonth,
  removeCommas,
  roundTwoDecimals
} from '../../util';
import { createDefaultRow, splitInputAcrossWeeks } from '../../services/table';
import {
  BaseWeeklyPricingAttributes,
  getRatio,
  getWeekBasePrice,
  RATECARD
} from '../../services/calculate';

interface DefineDurationProps {
  burstLength: number;
  option: DEFINE_OPTION;
  weeks: Week[];
  burstName: DURATION_SECONDS;
}

const StyledDurationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  border-radius: 16px;
  background-color: ${COLORS.NEUTRAL_925};
  margin-bottom: 24px;
  padding: 8px 16px;
  color: ${COLORS.WHITE};
`;

const StyledDurationTopWrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  gap: 12px;
`;

const StyledIconContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  min-width: 200px;
  gap: 12px;
`;

const StyledDurationHeading = styled.h4`
  font-size: 27.65px;
  font-style: normal;
  font-weight: 700;
  line-height: 32px;
  letter-spacing: -0.829px;
  color: ${COLORS.NEUTRAL_200};
  margin: 0;
`;

const StyledChipContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 16px;
  padding-top: 9px;
  padding-bottom: 9px;
`;

const StyledExtraSection = styled.div``;

const DefineDuration = ({
  burstLength,
  option,
  weeks,
  burstName
}: DefineDurationProps) => {
  const dispatch = useAppDispatch();
  const [showExtraSection, setShowExtraSection] = useState<boolean>(false);
  const {
    campaignDurations,
    advertiserDiscount: discount,
    demographic
  } = useAppSelector((state: RootState) => state.detail);
  const { durationLoading, weeklyPricing } = useAppSelector(
    (state: RootState) => state.spreadsheet
  );
  const bursts = useAppSelector((state: RootState) => state.burst[burstName]);
  const { calculated, burst: allBursts } = useAppSelector(
    (state: RootState) => state
  );

  interface DefineOption {
    id: string;
    title: string;
    description: string;
    inputLabel: string;
    helperText: string;
    leftButtonLabel: string;
    rightButtonLabel: string;
    leftButtonValue: string;
    rightButtonValue: string;
    leftButtonIcon: ReactElement;
    rightButtonIcon: ReactElement;
  }

  const options: Record<string, DefineOption> = {
    [DEFINE_OPTION.TARPS]: {
      id: 'tarps',
      title: 'Enter Campaign TARPs',
      description:
        'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
      inputLabel: 'Campaign TARPs',
      helperText: 'Target Audience Rating Points (TARPs)',
      leftButtonLabel: 'Budget',
      rightButtonLabel: '000s',
      leftButtonValue: roundTwoDecimals(
        bursts.calculatedTotalBudget
      ).toLocaleString(undefined, { minimumFractionDigits: 2 }),
      rightButtonValue: roundTwoDecimals(
        bursts.calculatedTotalImpact
      ).toLocaleString(undefined, { minimumFractionDigits: 2 }),
      leftButtonIcon: <SavingsOutlined />,
      rightButtonIcon: <Zeros />
    },
    [DEFINE_OPTION.BUDGET]: {
      id: 'budget',
      title: 'Enter Campaign Budget',
      description:
        'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
      inputLabel: 'Campaign Budget',
      helperText: 'Currency: NZD',
      leftButtonLabel: 'TARPs',
      rightButtonLabel: '000s',
      leftButtonValue: roundTwoDecimals(
        bursts.calculatedTotalTarps
      ).toLocaleString(undefined, { minimumFractionDigits: 2 }),
      rightButtonValue: roundTwoDecimals(
        bursts.calculatedTotalImpact
      ).toLocaleString(undefined, { minimumFractionDigits: 2 }),
      leftButtonIcon: <CrisisAlert />,
      rightButtonIcon: <Zeros />
    },
    [DEFINE_OPTION.ZEROS]: {
      id: 'zeros',
      title: 'Enter Campaign Impacts (000s)',
      description:
        'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
      inputLabel: 'Campaign 000s Target',
      helperText: '000s of Impacts',
      leftButtonLabel: 'Budget',
      rightButtonLabel: 'TARPs',
      leftButtonValue: roundTwoDecimals(
        bursts.calculatedTotalBudget
      ).toLocaleString(undefined, { minimumFractionDigits: 2 }),
      rightButtonValue: roundTwoDecimals(
        bursts.calculatedTotalTarps
      ).toLocaleString(undefined, { minimumFractionDigits: 2 }),
      leftButtonIcon: <SavingsOutlined />,
      rightButtonIcon: <CrisisAlert />
    }
  };

  const toggleExtraSection = () => {
    setShowExtraSection(!showExtraSection);
  };

  const handleSplitBurstAcrossActiveWeeks = () => {
    const rows = retrieveUpdatedRows();

    if (rows.length === 0) {
      return;
    }

    if (rows) {
      updateBurstDisplaySummaries(rows);
    }
  };

  const updateBurstDisplaySummaries = (rows: rowStructure[]) => {
    dispatch(setAllRows({ burst: burstName, rows }));

    dispatch(
      updateTotalCalculatedBudget({
        calculatedTotalBudget: updateBudgetDisplay(rows),
        burst: burstName
      })
    );
    dispatch(
      updateTotalCalculatedTarps({
        calculatedTotalTarps: updateTarpsDisplay(rows),
        burst: burstName
      })
    );
    dispatch(
      updateTotalCalculatedImpacts({
        calculatedTotalImpact: updateImpactDisplay(rows),
        burst: burstName
      })
    );
  };

  const retrieveUpdatedRows = (): rowStructure[] => {
    let totalToSplit = 0;

    switch (option) {
      case DEFINE_OPTION.BUDGET:
        totalToSplit = bursts.budget;
        break;
      case DEFINE_OPTION.TARPS:
        totalToSplit = bursts.tarps;
        break;
      case DEFINE_OPTION.ZEROS:
        totalToSplit = bursts.impact;
        break;
      default:
        return [];
    }

    return splitInputAcrossWeeks(bursts.rows, option, totalToSplit);
  };

  const updateTarpsDisplay = (rows: rowStructure[]): number => {
    let totalTarps = 0;
    rows.map((row) => {
      totalTarps += row.tarps;
    });
    return totalTarps;
  };

  const updateImpactDisplay = (rows: rowStructure[]): number => {
    let totalImpact = 0;
    rows.map((row) => {
      totalImpact += row.impact;
    });
    return totalImpact;
  };

  const updateBudgetDisplay = (rows: rowStructure[]): number => {
    let totalBudget = 0;
    rows.map((row) => {
      totalBudget += row.budget;
    });
    return totalBudget;
  };

  const handleInputChange = (value: string) => {
    if (!isOnlyNumbers(value)) {
      return;
    }

    switch (option) {
      case DEFINE_OPTION.BUDGET:
        dispatch(
          updateBudget({
            budget: Number(removeCommas(value)),
            burst: burstName
          })
        );
        break;
      case DEFINE_OPTION.TARPS:
        dispatch(
          updateTarps({ tarps: Number(removeCommas(value)), burst: burstName })
        );
        break;
      case DEFINE_OPTION.ZEROS:
        dispatch(
          updateImpact({
            impact: Number(removeCommas(value)),
            burst: burstName
          })
        );
        break;
      default:
        return '';
    }
  };

  const currentOption = options[option];

  const displayInputValue = (): string => {
    switch (option) {
      case DEFINE_OPTION.BUDGET:
        return formatDisplayInputValue(bursts.budget);
      case DEFINE_OPTION.TARPS:
        return formatDisplayInputValue(bursts.tarps);
      case DEFINE_OPTION.ZEROS:
        return formatDisplayInputValue(bursts.impact);
      default:
        return '';
    }
  };

  const formatDisplayInputValue = (value: number): string => {
    if (value == 0) {
      return '';
    }

    return value.toLocaleString();
  };

  const initializeNewRows = (burst: DURATION_SECONDS): rowStructure[] => {
    const rows: rowStructure[] = [];
    weeks.map((week, id) => {
      const month = getMonthName(week.month);
      const lowercaseMonth = mapPascalMonthToLowercaseMonth(month);

      // get price(s) for a week
      const totalCPT = calculateTotalPrice(
        RATECARD.CPT,
        week.weekIndex,
        lowercaseMonth
      );
      const totalCPM = calculateTotalPrice(
        RATECARD.CPM,
        week.weekIndex,
        lowercaseMonth
      );

      // apply ratio
      const modifier = getRatio(durationLoading, burst as DURATION_SECONDS);
      const cpt = totalCPT * modifier;
      const cpm = totalCPM * modifier;

      // add new row
      const row = createDefaultRow(id, cpt, cpm, week);
      rows.push(row);
    });

    return rows;
  };

  const getUpdatedRows = (
    burst: DURATION_SECONDS,
    existingRows: rowStructure[]
  ): rowStructure[] => {
    const rowsToCompare = initializeNewRows(burst as DURATION_SECONDS);

    const newRowsWithExistingValues = existingRows.map((existingRow, index) => {
      const hasChanged =
        existingRow.cpt !== rowsToCompare[index].cpt ||
        existingRow.cpm !== rowsToCompare[index].cpm;

      if (hasChanged) {
        switch (option) {
          case DEFINE_OPTION.BUDGET:
            if (existingRow.budget > 0) {
              rowsToCompare[index].budget = existingRow.budget;
              rowsToCompare[index].tarps =
                existingRow.budget / rowsToCompare[index].cpt;
              rowsToCompare[index].impact =
                existingRow.budget / rowsToCompare[index].cpm;
            }
            break;
          case DEFINE_OPTION.TARPS:
            if (existingRow.tarps > 0) {
              rowsToCompare[index].tarps = existingRow.tarps;
              rowsToCompare[index].budget =
                existingRow.tarps * rowsToCompare[index].cpt;
              rowsToCompare[index].impact =
                (existingRow.tarps * rowsToCompare[index].cpt) /
                rowsToCompare[index].cpm;
            }
            break;
          case DEFINE_OPTION.ZEROS:
            if (existingRow.impact > 0) {
              rowsToCompare[index].impact = existingRow.impact;
              rowsToCompare[index].budget =
                existingRow.impact * rowsToCompare[index].cpm;
              rowsToCompare[index].tarps =
                (existingRow.impact * rowsToCompare[index].cpm) /
                rowsToCompare[index].cpt;
            }
            break;
        }
      }

      return {
        ...rowsToCompare[index],
        ...existingRow,
        cpt: rowsToCompare[index].cpt,
        cpm: rowsToCompare[index].cpm,
        budget: rowsToCompare[index].budget,
        tarps: rowsToCompare[index].tarps,
        impact: rowsToCompare[index].impact
      };
    });

    return newRowsWithExistingValues;
  };

  const setUpTable = (): void => {
    Object.entries(campaignDurations).forEach(([burst, isBurstSelected]) => {
      const existingRows = allBursts[burst as DURATION_SECONDS].rows;

      // don't set up initial row values if data already exists
      if (existingRows.length > 0) {
        const newRowsWithExistingValues = getUpdatedRows(
          burst as DURATION_SECONDS,
          existingRows
        );

        dispatch(
          setAllRows({
            burst: burst as DURATION_SECONDS,
            rows: newRowsWithExistingValues
          })
        );

        return;
      }

      // initialise a new row if the burst is selected
      if (isBurstSelected) {
        const newRows = initializeNewRows(burst as DURATION_SECONDS);

        dispatch(
          setAllRows({
            burst: burst as DURATION_SECONDS,
            rows: newRows
          })
        );
      }
    });
  };

  const calculateTotalPrice = (
    ratecard: RATECARD,
    week: number,
    month: MONTH_LOWERCASE
  ): number => {
    if (!demographic) return 0;

    const attributes: BaseWeeklyPricingAttributes = {
      demographic,
      ratecard,
      week,
      discount
    };

    const basePrice = getWeekBasePrice(weeklyPricing, attributes);
    const totalPrice =
      basePrice * ((calculated[month].totalLoading + 100) / 100);

    return totalPrice;
  };

  const sumAllTarps = () => {
    let summedTarps = 0;
    bursts.rows.map((row) => {
      if (row.active) {
        summedTarps += row.tarps;
      }
    });

    dispatch(
      updateTotalCalculatedTarps({
        calculatedTotalTarps: summedTarps,
        burst: burstName
      })
    );
  };

  const sumAllImpacts = () => {
    let summedImpacts = 0;
    bursts.rows.map((row) => {
      if (row.active) {
        summedImpacts += row.impact;
      }
    });

    dispatch(
      updateTotalCalculatedImpacts({
        calculatedTotalImpact: summedImpacts,
        burst: burstName
      })
    );
  };

  const sumAllBudgets = () => {
    let summedBudgets = 0;
    bursts.rows.map((row) => {
      if (row.active) {
        summedBudgets += row.budget;
      }
    });

    dispatch(
      updateTotalCalculatedBudget({
        calculatedTotalBudget: summedBudgets,
        burst: burstName
      })
    );
  };

  const updateTotals = (): void => {
    sumAllTarps();
    sumAllImpacts();
    sumAllBudgets();
  };

  useEffect(() => {
    updateTotals();
  }, [bursts.rows]);

  useEffect(() => {
    if (bursts.budget || bursts.impact || bursts.tarps) {
      setShowExtraSection(true);
    }
  }, [bursts.budget, bursts.impact, bursts.tarps]);

  useEffect(() => {
    setUpTable();
  }, []);

  return (
    <StyledDurationContainer>
      <StyledDurationTopWrapper>
        <StyledIconContainer onClick={toggleExtraSection}>
          {showExtraSection ? <ArrowDropDown /> : <ArrowRight />}
          <TimerOutlined />
          <StyledDurationHeading>{burstLength} Burst</StyledDurationHeading>
        </StyledIconContainer>
        <Input
          label={currentOption.title}
          value={displayInputValue()}
          onChange={handleInputChange}
        />
        <StyledChipContainer>
          <DefineInputChip
            description={currentOption.leftButtonLabel}
            value={currentOption.leftButtonValue}
            icon={currentOption.leftButtonIcon}
            compactView
          />
          <DefineInputChip
            description={currentOption.rightButtonLabel}
            value={currentOption.rightButtonValue}
            icon={currentOption.rightButtonIcon}
            compactView
          />
        </StyledChipContainer>
      </StyledDurationTopWrapper>
      {showExtraSection && (
        <StyledExtraSection>
          <Grid
            sx={{ display: 'flex', justifyContent: 'flex-end' }}
            item
            xs={6}
          >
            <Button
              icon={<CallSplitOutlined />}
              label="Split Burst across active weeks"
              backgroundColor={COLORS.PRIMARY_500}
              hoverBackgroundColor={COLORS.BUTTON_PRIMARY_HOVER}
              textColor={COLORS.PRIMARY_800}
              showShadow={false}
              onClick={() => {
                handleSplitBurstAcrossActiveWeeks();
              }}
            />
          </Grid>
          <DefineDurationTable option={option} burst={burstName} />
        </StyledExtraSection>
      )}
    </StyledDurationContainer>
  );
};

export default DefineDuration;
